import React, { useEffect, useRef, useState } from 'react';
import { ScanBarcode, ArrowDownCircle, ArrowUpCircle, Package, X } from 'lucide-react';
import toast from 'react-hot-toast';

/**
 * USB/el terminali barkod okuyucular klavye gibi çalışır.
 * Odaklı input + Enter ile tarama tamamlanır.
 */
const BarcodeScanPanel = ({
  title = 'Barkod Tarama',
  subtitle = 'Barkodu okutun veya yazıp Enter’a basın',
  mode = 'out',
  allowModeSwitch = true,
  warehouses = [],
  orders = [],
  defaultWarehouseId = '',
  defaultOrderId = '',
  requireOrder = false,
  quantityLabel = 'Miktar',
  onLookup,
  onConfirm,
  recentLimit = 8,
}) => {
  const inputRef = useRef(null);
  const [barcode, setBarcode] = useState('');
  const [movementType, setMovementType] = useState(mode);
  const [quantity, setQuantity] = useState(1);
  const [warehouseId, setWarehouseId] = useState(defaultWarehouseId || '');
  const [orderId, setOrderId] = useState(defaultOrderId || '');
  const [material, setMaterial] = useState(null);
  const [lookingUp, setLookingUp] = useState(false);
  const [saving, setSaving] = useState(false);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    setMovementType(mode);
  }, [mode]);

  useEffect(() => {
    if (defaultWarehouseId) setWarehouseId(String(defaultWarehouseId));
  }, [defaultWarehouseId]);

  useEffect(() => {
    if (defaultOrderId) setOrderId(String(defaultOrderId));
  }, [defaultOrderId]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const focusScanner = () => {
    inputRef.current?.focus();
  };

  const clearScan = () => {
    setBarcode('');
    setMaterial(null);
    setQuantity(1);
    focusScanner();
  };

  const lookupBarcode = async (value) => {
    const code = String(value || '').trim();
    if (!code) return;

    setLookingUp(true);
    try {
      const result = await onLookup(code);
      setMaterial(result);
      if (result?.warehouse_id && !warehouseId) {
        setWarehouseId(String(result.warehouse_id));
      }
      toast.success(`${result.name} bulundu`);
    } catch (error) {
      setMaterial(null);
      toast.error(error.response?.data?.error || 'Barkod bulunamadı');
    } finally {
      setLookingUp(false);
      focusScanner();
    }
  };

  const handleScanKeyDown = async (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    await lookupBarcode(barcode);
  };

  const handleConfirm = async () => {
    if (!material) {
      toast.error('Önce barkod okutun');
      return;
    }
    if (requireOrder && !orderId) {
      toast.error('Üretim emri seçin');
      return;
    }
    if (!quantity || Number(quantity) <= 0) {
      toast.error('Geçerli miktar girin');
      return;
    }

    setSaving(true);
    try {
      const result = await onConfirm({
        barcode: barcode.trim() || material.barcode || material.code,
        material,
        movement_type: movementType,
        quantity: Number(quantity),
        warehouse_id: warehouseId || undefined,
        order_id: orderId || undefined,
      });

      const entry = {
        id: `${Date.now()}-${Math.random()}`,
        name: material.name,
        code: material.code,
        barcode: material.barcode || barcode,
        type: movementType,
        quantity: Number(quantity),
        newQuantity: result?.new_quantity,
        at: new Date().toLocaleTimeString('tr-TR'),
      };
      setRecent((prev) => [entry, ...prev].slice(0, recentLimit));
      toast.success(result?.message || 'İşlem kaydedildi');
      clearScan();
    } catch (error) {
      toast.error(error.response?.data?.error || 'İşlem başarısız');
    } finally {
      setSaving(false);
      focusScanner();
    }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      <div className="flex items-start justify-between gap-4 border-b border-slate-100 bg-slate-50 px-5 py-4">
        <div className="flex items-start gap-3">
          <div className="rounded-xl bg-blue-100 p-3 text-blue-700">
            <ScanBarcode className="h-6 w-6" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-900">{title}</h2>
            <p className="text-sm text-slate-500">{subtitle}</p>
          </div>
        </div>
        {allowModeSwitch && (
          <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1">
            <button
              type="button"
              onClick={() => setMovementType('in')}
              className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold ${
                movementType === 'in' ? 'bg-green-600 text-white' : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              <ArrowDownCircle className="h-4 w-4" />
              Giriş
            </button>
            <button
              type="button"
              onClick={() => setMovementType('out')}
              className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold ${
                movementType === 'out' ? 'bg-red-600 text-white' : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              <ArrowUpCircle className="h-4 w-4" />
              Çıkış
            </button>
          </div>
        )}
      </div>

      <div className="grid gap-5 p-5 lg:grid-cols-2">
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Barkod</label>
            <div className="flex gap-2">
              <input
                ref={inputRef}
                value={barcode}
                onChange={(e) => setBarcode(e.target.value)}
                onKeyDown={handleScanKeyDown}
                placeholder="Barkodu okutun..."
                className="w-full rounded-xl border border-slate-300 px-4 py-3 text-lg font-semibold tracking-wide focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                autoComplete="off"
              />
              <button
                type="button"
                onClick={() => lookupBarcode(barcode)}
                disabled={lookingUp}
                className="rounded-xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
              >
                {lookingUp ? '...' : 'Bul'}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">{quantityLabel}</label>
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </div>
            {warehouses.length > 0 && (
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Depo</label>
                <select
                  value={warehouseId}
                  onChange={(e) => setWarehouseId(e.target.value)}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                >
                  <option value="">Otomatik / seçiniz</option>
                  {warehouses.map((wh) => (
                    <option key={wh.id} value={wh.id}>{wh.name}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {orders.length > 0 && (
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">
                Üretim Emri {requireOrder ? '*' : '(opsiyonel)'}
              </label>
              <select
                value={orderId}
                onChange={(e) => setOrderId(e.target.value)}
                className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
              >
                <option value="">Emir seçin...</option>
                {orders.map((order) => (
                  <option key={order.id} value={order.id}>
                    {order.order_number} — {order.product_name || 'Ürün'} ({order.status})
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleConfirm}
              disabled={saving || !material}
              className={`flex-1 rounded-xl px-4 py-3 text-sm font-bold text-white disabled:opacity-50 ${
                movementType === 'in' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'
              }`}
            >
              {saving
                ? 'Kaydediliyor...'
                : movementType === 'in'
                  ? 'Stok Girişi Yap'
                  : 'Stok Çıkışı Yap'}
            </button>
            <button
              type="button"
              onClick={clearScan}
              className="rounded-xl border border-slate-200 px-4 py-3 text-slate-600 hover:bg-slate-50"
              title="Temizle"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-4">
          {material ? (
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="rounded-xl bg-white p-3 text-slate-700 shadow-sm">
                  <Package className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Bulunan malzeme</p>
                  <p className="text-lg font-bold text-slate-900">{material.name}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="rounded-xl bg-white p-3">
                  <p className="text-slate-500">Kod</p>
                  <p className="font-semibold text-slate-900">{material.code}</p>
                </div>
                <div className="rounded-xl bg-white p-3">
                  <p className="text-slate-500">Barkod</p>
                  <p className="font-semibold text-slate-900">{material.barcode || '-'}</p>
                </div>
                <div className="rounded-xl bg-white p-3">
                  <p className="text-slate-500">Stok</p>
                  <p className="font-semibold text-slate-900">{material.quantity} {material.unit}</p>
                </div>
                <div className="rounded-xl bg-white p-3">
                  <p className="text-slate-500">Depo</p>
                  <p className="font-semibold text-slate-900">{material.warehouse_name || '-'}</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex h-full min-h-[180px] flex-col items-center justify-center text-center text-slate-500">
              <ScanBarcode className="mb-3 h-10 w-10 opacity-40" />
              <p className="font-medium">Barkod bekleniyor</p>
              <p className="mt-1 text-sm">Okuyucu ile tarayın veya yazıp Enter’a basın</p>
            </div>
          )}
        </div>
      </div>

      {recent.length > 0 && (
        <div className="border-t border-slate-100 px-5 py-4">
          <p className="mb-3 text-sm font-semibold text-slate-700">Son taramalar</p>
          <div className="space-y-2">
            {recent.map((item) => (
              <div key={item.id} className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2 text-sm">
                <div>
                  <span className="font-semibold text-slate-900">{item.name}</span>
                  <span className="ml-2 text-slate-500">{item.barcode}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                    item.type === 'in' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {item.type === 'in' ? 'Giriş' : 'Çıkış'} {item.quantity}
                  </span>
                  <span className="text-slate-400">{item.at}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BarcodeScanPanel;
